import { Link, useNavigate } from 'react-router-dom';
import AdListImage from './AdListImage';
import Button from 'components/Button';
import TestEdildiBadge from 'components/TestEdildiBadge';
import SellerTypeBadge from 'components/SellerTypeBadge';
import { AdListing } from 'types/adListing';
import { useListingContext } from 'contexts/ListingContext';
import { listingDescriptionPlainExcerpt } from 'utils/richTextContent';
import './AdList.css';

const PRODUCT_IMAGE_STYLE: React.CSSProperties = { width: '100%', aspectRatio: '4 / 3', objectFit: 'cover' };

const COMPUTER_SPECS: { key: 'islemci' | 'ekranKarti' | 'ram' | 'ssd' | 'hdd'; icon: string; prefix: string }[] = [
  { key: 'islemci', icon: 'fa-microchip', prefix: '' },
  { key: 'ekranKarti', icon: 'fa-tv', prefix: '' },
  { key: 'ram', icon: 'fa-memory', prefix: '' },
  { key: 'ssd', icon: 'fa-hdd', prefix: 'SSD: ' },
  { key: 'hdd', icon: 'fa-hdd', prefix: 'HDD: ' },
];

interface AdListGridItemProps {
  ilan: AdListing;
  detailLinkBase?: string;
  onToggleFavorite?: (id: string | number, e: React.MouseEvent) => void;
  displayFavorites?: boolean;
  displayEdit?: boolean;
  displayDelete?: boolean;
}

export default function AdListGridItem({ 
  ilan, 
  detailLinkBase = '/ilan-detay', 
  onToggleFavorite, 
  displayFavorites = true,
  displayEdit = false,
  displayDelete = false,
}: AdListGridItemProps) {
  const isFavorite = ilan.isUserFavorite === true;
  const navigate = useNavigate();
  const { setDeletingListingId, setShowDeleteModal } = useListingContext();
  const descriptionExcerpt = listingDescriptionPlainExcerpt(ilan.aciklama, 140);

  const handleEdit = () => {
    navigate(`/ilan-duzenle/${ilan.id}`);
  }

  const handleDelete = async (ilanId: string | number) => {
    setShowDeleteModal(true);
    setDeletingListingId(ilanId);
  }

  const detailLink = detailLinkBase ? `${detailLinkBase}/${ilan.id}` : `/ilan-detay/${ilan.id}`;

  return (
    <div className="col">
      <article className="sb-product-card">
        <div className="sb-product-card__media">
          <AdListImage ilan={ilan} style={PRODUCT_IMAGE_STYLE} detailLink={detailLink} />

          {ilan.isTested && (
            <span className="sb-product-card__badge sb-product-card__badge--top">
              <TestEdildiBadge compact />
            </span>
          )}

          {ilan.sellerAccountType && (
            <span className="sb-product-card__badge sb-product-card__badge--bottom">
              <SellerTypeBadge accountType={ilan.sellerAccountType} compact />
            </span>
          )}

          <div className="sb-product-card__actions">
            {displayFavorites && (
              <Button
                type="button"
                className={`sb-product-card__action favorite-btn ${isFavorite ? 'active' : ''}`}
                onClick={
                  onToggleFavorite
                    ? (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onToggleFavorite(ilan.id, e);
                      }
                    : undefined
                }
                aria-label={isFavorite ? 'Favorilerden çıkar' : 'Favorilere ekle'}
                aria-pressed={isFavorite}
              >
                <i className={`${isFavorite ? 'fas text-danger' : 'far'} fa-heart`} aria-hidden="true" />
              </Button>
            )}
            {displayEdit && (
              <Button
                type="button"
                className="sb-product-card__action favorite-btn"
                onClick={handleEdit}
                aria-label="İlanı düzenle"
              >
                <i className="fas fa-pencil text-primary" aria-hidden="true" />
              </Button>
            )}
            {displayDelete && (
              <Button
                type="button"
                className="sb-product-card__action favorite-btn"
                onClick={() => handleDelete(ilan.id)}
                aria-label="İlanı sil"
              >
                <i className="fas fa-trash text-danger" aria-hidden="true" />
              </Button>
            )}
          </div>
        </div>

        <div className="sb-product-card__body">
          <Link to={detailLink} className="sb-product-card__title-link">
            <h3 className="sb-product-card__title" title={ilan.baslik}>{ilan.baslik}</h3>
          </Link>

          {ilan.rejectionReason ? (
            <div className="alert alert-danger py-2 px-2 small mb-2" role="status">
              <i className="fas fa-times-circle me-1" aria-hidden />
              <strong>Red gerekçesi:</strong> {ilan.rejectionReason}
            </div>
          ) : null}

          {descriptionExcerpt ? (
            <p className="sb-product-card__desc">{descriptionExcerpt}</p>
          ) : null}

          {ilan.kategori === "Bilgisayar" && (
            <ul className="sb-product-card__specs">
              {COMPUTER_SPECS.map(({ key, icon, prefix }) =>
                ilan[key] ? (
                  <li key={key}>
                    <i className={`fas ${icon}`} aria-hidden="true" />
                    <span>{prefix}{ilan[key]}</span>
                  </li>
                ) : null
              )}
            </ul>
          )}

          <div className="sb-product-card__footer">
            <span className="sb-product-card__location" title={ilan.konum}>
              <i className="fas fa-map-marker-alt" aria-hidden="true" />
              {ilan.konum}
            </span>
            <span className="sb-product-card__price">{ilan.fiyat.toLocaleString('tr-TR')} ₺</span>
          </div>
        </div>
      </article>
    </div>
  );
}